import { useState } from 'react'
import {
  TrendingUp,
  Clock,
  MessageSquare,
  FileText,
  Download,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

// Sample data
const queryTrend = [
  { date: 'Jan 1', queries: 145 },
  { date: 'Jan 2', queries: 187 },
  { date: 'Jan 3', queries: 156 },
  { date: 'Jan 4', queries: 203 },
  { date: 'Jan 5', queries: 178 },
  { date: 'Jan 6', queries: 62 },
  { date: 'Jan 7', queries: 45 },
  { date: 'Jan 8', queries: 189 },
  { date: 'Jan 9', queries: 234 },
  { date: 'Jan 10', queries: 198 },
  { date: 'Jan 11', queries: 223 },
  { date: 'Jan 12', queries: 245 },
  { date: 'Jan 13', queries: 89 },
  { date: 'Jan 14', queries: 67 },
]

const responseTimeData = [
  { date: 'Jan 1', avg: 1.2 },
  { date: 'Jan 2', avg: 1.1 },
  { date: 'Jan 3', avg: 1.4 },
  { date: 'Jan 4', avg: 1.0 },
  { date: 'Jan 5', avg: 1.1 },
  { date: 'Jan 6', avg: 1.3 },
  { date: 'Jan 7', avg: 1.2 },
]

const queryByManual = [
  { name: 'B737 AMM', queries: 456, percentage: 35 },
  { name: 'A320 CMM', queries: 324, percentage: 25 },
  { name: 'B777 SB', queries: 259, percentage: 20 },
  { name: 'B787 FIM', queries: 156, percentage: 12 },
  { name: 'Other', queries: 104, percentage: 8 },
]

const queryByUser = [
  { name: 'John Martinez', queries: 342, role: 'Owner' },
  { name: 'Sarah Chen', queries: 256, role: 'Admin' },
  { name: 'Mike Thompson', queries: 189, role: 'Technician' },
  { name: 'David Park', queries: 145, role: 'Technician' },
  { name: 'Emily Davis', queries: 98, role: 'Technician' },
]

const queryByCategory = [
  { name: 'Maintenance Procedures', value: 35, color: '#1a365d' },
  { name: 'Specifications', value: 25, color: '#2c5282' },
  { name: 'Troubleshooting', value: 20, color: '#3182ce' },
  { name: 'Parts Information', value: 12, color: '#4299e1' },
  { name: 'Other', value: 8, color: '#90cdf4' },
]

export function AnalyticsPage() {
  const [dateRange, setDateRange] = useState('7d')

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">
            Usage metrics and insights for your organization
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Queries"
          value="2,847"
          change="+12.5%"
          changeType="positive"
          icon={MessageSquare}
        />
        <MetricCard
          title="Avg Response Time"
          value="1.2s"
          change="-0.3s"
          changeType="positive"
          icon={Clock}
        />
        <MetricCard
          title="Manuals Queried"
          value="38"
          change="+2"
          changeType="neutral"
          icon={FileText}
        />
        <MetricCard
          title="Active Users"
          value="12"
          change="+3"
          changeType="positive"
          icon={TrendingUp}
        />
      </div>

      {/* Charts */}
      <Tabs defaultValue="queries" className="space-y-6">
        <TabsList>
          <TabsTrigger value="queries">Query Volume</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="queries" className="space-y-6">
          {/* Query Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Query Volume Over Time</CardTitle>
              <CardDescription>Daily query count for the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={queryTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a365d',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="queries"
                      stroke="#2c5282"
                      strokeWidth={2}
                      dot={{ fill: '#2c5282', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Top Manuals */}
            <Card>
              <CardHeader>
                <CardTitle>Queries by Manual</CardTitle>
                <CardDescription>Most frequently queried documentation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {queryByManual.map((manual) => (
                    <div key={manual.name} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{manual.name}</span>
                          <span className="text-sm text-gray-500">{manual.queries}</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100">
                          <div
                            className="h-2 rounded-full bg-navy-900"
                            style={{ width: `${manual.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Users */}
            <Card>
              <CardHeader>
                <CardTitle>Top Users</CardTitle>
                <CardDescription>Most active team members</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {queryByUser.map((user, index) => (
                    <div key={user.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.role}</p>
                        </div>
                      </div>
                      <span className="font-medium text-gray-900">{user.queries}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Average Response Time</CardTitle>
              <CardDescription>Query processing time over the period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={responseTimeData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" unit="s" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a365d',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                      }}
                      formatter={(value: number) => [`${value}s`, 'Avg Time']}
                    />
                    <Bar dataKey="avg" fill="#2c5282" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900">99.9%</div>
                  <div className="mt-1 text-sm text-gray-500">Uptime</div>
                  <Badge variant="success" className="mt-2">Excellent</Badge>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900">0.87</div>
                  <div className="mt-1 text-sm text-gray-500">Avg Retrieval Score</div>
                  <Badge variant="info" className="mt-2">High Relevance</Badge>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-gray-900">4.2K</div>
                  <div className="mt-1 text-sm text-gray-500">Tokens/Query Avg</div>
                  <Badge variant="secondary" className="mt-2">Normal</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="breakdown" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Query Categories Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Query Categories</CardTitle>
                <CardDescription>Types of questions being asked</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={queryByCategory}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {queryByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  {queryByCategory.map((cat) => (
                    <div key={cat.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span>{cat.name}</span>
                      </div>
                      <span className="font-medium">{cat.value}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Source Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Document Source</CardTitle>
                <CardDescription>Native vs external DMS queries</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Native (Pinecone/S3)</span>
                      <span className="font-bold">78%</span>
                    </div>
                    <div className="h-4 rounded-full bg-gray-100">
                      <div
                        className="h-4 rounded-full bg-navy-900"
                        style={{ width: '78%' }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">2,221 queries</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">External DMS</span>
                      <span className="font-bold">22%</span>
                    </div>
                    <div className="h-4 rounded-full bg-gray-100">
                      <div
                        className="h-4 rounded-full bg-voltaire-blue"
                        style={{ width: '22%' }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">626 queries</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function MetricCard({
  title,
  value,
  change,
  changeType,
  icon: Icon,
}: {
  title: string
  value: string
  change: string
  changeType: 'positive' | 'negative' | 'neutral'
  icon: React.ElementType
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
            <p
              className={`mt-1 text-sm ${
                changeType === 'positive'
                  ? 'text-green-600'
                  : changeType === 'negative'
                  ? 'text-red-600'
                  : 'text-gray-500'
              }`}
            >
              {change}
            </p>
          </div>
          <div className="rounded-full bg-navy-900/10 p-3">
            <Icon className="h-6 w-6 text-navy-900" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
